import { Table, TablePaginationConfig } from "antd"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { useCampaignsColumns } from "../hooks/useCampaignsColumns"
import {
  EXPAND_COLUMN_WIDTH,
  SELECTION_COLUMN_WIDTH,
  TABLE_SCROLL_X,
} from "../constants/columns.constants"
import { Key, useState } from "react"
import { ICampaignResult } from "../types/sbCampaignApi.types"
import { SponsoredBrandsTargetsTable } from "./SponsoredBrandsTargetsTable"
import { setStateType } from "../../../types/setStateType"
import { FilterValue, SorterResult, TableCurrentDataSource } from "antd/es/table/interface"

interface ISponsoredBrandsTable {
  campaigns: ICampaignResult[]
  expandedRowCampaignIds: readonly Key[]
  setExpandedRowCampaignIds: setStateType<readonly Key[]>
  selectedTargetIds: Key[]
  setSelectedTargetIds: setStateType<Key[]>
  setCampaignDataForTable: setStateType<ICampaignResult[] | null>
  setSelectedRowCampaignIds: setStateType<Key[]>
  selectedRowCampaignIds: Key[]
}

export const SponsoredBrandsTable: React.FC<ISponsoredBrandsTable> = ({
  campaigns,
  expandedRowCampaignIds,
  setExpandedRowCampaignIds,
  selectedTargetIds,
  setSelectedTargetIds,
  setCampaignDataForTable,
  setSelectedRowCampaignIds,
  selectedRowCampaignIds,
}) => {
  const { loading } = useTypedSelector((select) => select.sponsoredBrands)
  const [filteredCampaignData, setFilteredCampaignData] = useState<ICampaignResult[] | null>(null)

  const columns = useCampaignsColumns(setCampaignDataForTable)

  function ownCampaignIds(): Key[] {
    return campaigns.map((campaign) => campaign.campaignId)
  }

  function onExpandedRowsChange(expandedKeys: readonly Key[]) {
    const ownIds = ownCampaignIds()
    setExpandedRowCampaignIds((prev) => [
      ...prev.filter((id) => !ownIds.includes(id)),
      ...expandedKeys,
    ])
  }

  function handleOnChangeFilter(
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICampaignResult> | SorterResult<ICampaignResult>[],
    extra: TableCurrentDataSource<ICampaignResult>
  ) {
    setFilteredCampaignData(extra.currentDataSource)
  }

  function handleRowSelectionChange(campaignIds: Key[]) {
    const ownIds = ownCampaignIds()
    setSelectedRowCampaignIds((prev) => [
      ...prev.filter((id) => !ownIds.includes(id)),
      ...campaignIds,
    ])
  }

  function onSelectAll() {
    const ownIds = ownCampaignIds()
    const filteredIds = (filteredCampaignData ?? campaigns).map((campaign) => campaign.campaignId)

    setSelectedRowCampaignIds((prev) => [
      ...prev.filter((id) => !ownIds.includes(id)),
      ...filteredIds,
    ])
  }

  return (
    <Table
      columns={columns}
      dataSource={campaigns}
      rowKey="campaignId"
      tableLayout="fixed"
      pagination={false}
      expandable={{
        indentSize: 5,
        columnWidth: EXPAND_COLUMN_WIDTH,
        expandedRowRender: (data) => (
          <SponsoredBrandsTargetsTable
            campaignId={data.campaignId}
            campaignName={data.campaignName}
            selectedTargetIds={selectedTargetIds}
            setSelectedTargetIds={setSelectedTargetIds}
            selectedRowCampaignIds={selectedRowCampaignIds}
            setSelectedRowCampaignIds={setSelectedRowCampaignIds}
          />
        ),
        expandedRowKeys: expandedRowCampaignIds,
        onExpandedRowsChange: onExpandedRowsChange,
      }}
      rowSelection={{
        selectedRowKeys: selectedRowCampaignIds,
        onChange: handleRowSelectionChange,
        onSelectAll: onSelectAll,
        columnWidth: SELECTION_COLUMN_WIDTH,
      }}
      onChange={handleOnChangeFilter}
      scroll={{ x: TABLE_SCROLL_X }}
      loading={loading}
    />
  )
}
